import { apiResponseType } from "@/Api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Filter, Trash2, X, ChevronDown } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PAYMENT_FORMS } from "@/utils/constants";
import { Checkbox } from "@/components/ui/checkbox";

export type FilterProps = {
  updateFront: (response: any) => void;
  setState: Dispatch<SetStateAction<string>>;
  funcSearch: (params: any) => apiResponseType;
  transactionType?: number;
};

type FilterItem = {
  key: string;
  label: string;
  value: string;
};

const TRANSACTION_TYPES = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

const RECONCILIATION_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  DIVERGENT: 3,
  REJECTED: 4,
  APPROVED_WITH_DISCLAIMER: 5,
  APPROVED_MANUAL: 6,
};

const PERIOD_OPTIONS = [
  { label: "Hoje", days: 0 },
  { label: "Últimos 7 dias", days: 7 },
  { label: "Últimos 30 dias", days: 30 },
  { label: "Este mês", days: "current-month" },
  { label: "Personalizado", days: "custom" },
];

const formSchema = z.object({
  name: z.string().optional(),
  financial_transaction_type_id: z.string().optional(),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
  reconciliation_status_id: z.string().optional(),
  payment_form_ids: z.array(z.string()).optional(),
});

const PAYMENT_FORMS_OPTIONS = [
  { id: PAYMENT_FORMS.CREDIT_CARD, name: "Cartão de Crédito" },
  { id: PAYMENT_FORMS.MONEY, name: "Dinheiro" },
  { id: PAYMENT_FORMS.PIX, name: "PIX Sicredi" },
  { id: PAYMENT_FORMS.PIX_BTG, name: "PIX BTG" },
  { id: PAYMENT_FORMS.PIX_CNPJ, name: "PIX CNPJ" },
  { id: PAYMENT_FORMS.VALE, name: "Vale Compra" },
  { id: PAYMENT_FORMS.PIX_MAQUINETA, name: "Pix Control Pay" },
  { id: PAYMENT_FORMS.INSTALLMENT_CARD, name: "Cartão Parcelado" },
  { id: PAYMENT_FORMS.DEPOSIT, name: "Depósito" },
  { id: PAYMENT_FORMS.ADVANCE_PAYMENT, name: "Pagamento Antecipado" },
];

// Todas as formas de pagamento exibíveis na conciliação, exceto Reinvestimento.
// Usado como filtro padrão quando nenhuma forma específica está selecionada,
// para nunca deixar registros de Reinvestimento aparecerem na listagem.
const ALL_PAYMENT_FORM_IDS_EXCEPT_REINVESTMENT = PAYMENT_FORMS_OPTIONS.map((f) =>
  f.id.toString()
);

export function FiltersView({
  onFilter,
  noFormName,
}: {
  onFilter: (data: any) => void;
  noFormName?: boolean;
}) {
  const [receivableType, setReceivableType] = useState<string>("all");
  const [selectedPaymentForms, setSelectedPaymentForms] = useState<string[]>([PAYMENT_FORMS.MONEY.toString()]);
  const [perPage, setPerPage] = useState<string>("100");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      financial_transaction_type_id: undefined,
      start_date: undefined,
      end_date: undefined,
      reconciliation_status_id: undefined,
      payment_form_ids: [],
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const formattedData = {
      ...values,
      start_date: values.start_date
        ? format(values.start_date, "yyyy-MM-dd")
        : undefined,
      end_date: values.end_date
        ? format(values.end_date, "yyyy-MM-dd")
        : undefined,
      financial_transaction_type_id:
        values.financial_transaction_type_id === "all"
          ? undefined
          : values.financial_transaction_type_id,
      reconciliation_status_id:
        values.reconciliation_status_id === "all"
          ? undefined
          : values.reconciliation_status_id,
      payment_form_ids:
        selectedPaymentForms.length > 0
          ? selectedPaymentForms
          : ALL_PAYMENT_FORM_IDS_EXCEPT_REINVESTMENT,
      receivable_type: receivableType === "all" ? undefined : receivableType,
      per_page: perPage,
    };
    onFilter(formattedData);
  };

  const handleClearFilters = () => {
    form.reset();
    setReceivableType("all");
    setSelectedPaymentForms([PAYMENT_FORMS.MONEY.toString()]);
    setPerPage("100");
    onFilter({ payment_form_ids: [PAYMENT_FORMS.MONEY.toString()], per_page: "100" });
  };

  const togglePaymentForm = (id: string) => {
    setSelectedPaymentForms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handlePerPageChange = (value: string) => {
    setPerPage(value);
    const values = form.getValues();
    const formattedData = {
      ...values,
      start_date: values.start_date
        ? format(values.start_date, "yyyy-MM-dd")
        : undefined,
      end_date: values.end_date
        ? format(values.end_date, "yyyy-MM-dd")
        : undefined,
      financial_transaction_type_id:
        values.financial_transaction_type_id === "all"
          ? undefined
          : values.financial_transaction_type_id,
      reconciliation_status_id:
        values.reconciliation_status_id === "all"
          ? undefined
          : values.reconciliation_status_id,
      payment_form_ids:
        selectedPaymentForms.length > 0
          ? selectedPaymentForms
          : ALL_PAYMENT_FORM_IDS_EXCEPT_REINVESTMENT,
      receivable_type: receivableType === "all" ? undefined : receivableType,
      per_page: value,
    };
    onFilter(formattedData);
  };

  const getSelectedPaymentFormsLabel = () => {
    if (selectedPaymentForms.length === 0) return "Selecione as formas";
    if (selectedPaymentForms.length === 1) {
      const form = PAYMENT_FORMS_OPTIONS.find(
        (f) => f.id.toString() === selectedPaymentForms[0]
      );
      return form?.name || "1 selecionada";
    }
    return `${selectedPaymentForms.length} selecionadas`;
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="bg-white dark:bg-zinc-800 rounded-lg p-4 mb-4 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {!noFormName && (
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome da Conciliação</FormLabel>
                  <FormControl>
                    <Input placeholder="Buscar por nome..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="reconciliation_status_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status da Conciliação</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.PENDING.toString()}
                    >
                      Pendente
                    </SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.APPROVED.toString()}
                    >
                      Aprovado
                    </SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.APPROVED_MANUAL.toString()}
                    >
                      Aprovado Manualmente
                    </SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.DIVERGENT.toString()}
                    >
                      Divergente
                    </SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.REJECTED.toString()}
                    >
                      Rejeitado
                    </SelectItem>
                    <SelectItem
                      value={RECONCILIATION_STATUS.APPROVED_WITH_DISCLAIMER.toString()}
                    >
                      Aprovado com Ressalvas
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormItem>
            <FormLabel>Forma de Pagamento</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full justify-between font-normal",
                    selectedPaymentForms.length === 0 && "text-muted-foreground"
                  )}
                >
                  {getSelectedPaymentFormsLabel()}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0" align="start">
                <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto">
                  {PAYMENT_FORMS_OPTIONS.map((paymentForm) => (
                    <div
                      key={paymentForm.id}
                      className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded cursor-pointer"
                      onClick={() => togglePaymentForm(paymentForm.id.toString())}
                    >
                      <Checkbox
                        checked={selectedPaymentForms.includes(
                          paymentForm.id.toString()
                        )}
                        onCheckedChange={() =>
                          togglePaymentForm(paymentForm.id.toString())
                        }
                      />
                      <span className="text-sm">{paymentForm.name}</span>
                    </div>
                  ))}
                </div>
                {selectedPaymentForms.length > 0 && (
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setSelectedPaymentForms([])}
                    >
                      Limpar seleção
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </FormItem>

          <FormItem>
            <FormLabel>Tipo de Recebível</FormLabel>
            <Select
              value={receivableType}
              onValueChange={setReceivableType}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="venda">Recebível de venda</SelectItem>
                <SelectItem value="marketplace">Recebível de venda marketplace</SelectItem>
              </SelectContent>
            </Select>
          </FormItem>

          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Data Inicial</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "dd/MM/yyyy", { locale: ptBR })
                        ) : (
                          <span>Selecione uma data</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Data Final</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "dd/MM/yyyy", { locale: ptBR })
                        ) : (
                          <span>Selecione uma data</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Mostrar</span>
            <Select value={perPage} onValueChange={handlePerPageChange}>
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">registros</span>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleClearFilters}>
              Limpar Filtros
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/80">
              Filtrar
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

export const TransactionFilters = ({
  updateFront,
  setState,
  funcSearch,
  transactionType: initialTransactionType,
}: FilterProps) => {
  const today = new Date();
  const [paymentDateStart, setPaymentDateStart] = useState<Date>(
    subDays(today, 7)
  );
  const [paymentDateEnd, setPaymentDateEnd] = useState<Date>(today);
  const [dueDateStart, setDueDateStart] = useState<Date>(subDays(today, 7));
  const [dueDateEnd, setDueDateEnd] = useState<Date>(today);

  const [transactionType, setTransactionType] = useState<number | undefined>(
    initialTransactionType
  );
  const [transactionTypeValue, setTransactionTypeValue] = useState<string>(
    initialTransactionType ? initialTransactionType.toString() : "all"
  );
  const [periodOption, setPeriodOption] = useState("7");
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterItem[]>([]);

  const [description, setDescription] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [expenseId, setExpenseId] = useState<string>("none");
  const [reconciliationStatusId, setReconciliationStatusId] =
    useState<string>("all");
  const [paymentFormId, setPaymentFormId] = useState<string>("all");

  const [activeDateFilter, setActiveDateFilter] = useState<"payment" | "due">(
    "payment"
  );

  useEffect(() => {
    setTransactionType(initialTransactionType);
    setTransactionTypeValue(
      initialTransactionType ? initialTransactionType.toString() : "all"
    );

    if (initialTransactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
      setExpenseId("none");
      setActiveDateFilter("payment");
    }
  }, [initialTransactionType]);

  const updateActiveFilters = () => {
    const newFilters: FilterItem[] = [];

    if (transactionType) {
      const label =
        transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE
          ? "Contas a Pagar"
          : "Contas a Receber";
      newFilters.push({
        key: "transaction_type_id",
        label: "Tipo",
        value: label,
      });
    }

    if (description) {
      newFilters.push({
        key: "description",
        label: "Descrição",
        value: description,
      });
    }

    if (amount) {
      newFilters.push({
        key: "amount",
        label: "Valor",
        value: amount,
      });
    }

    if (reconciliationStatusId !== "all") {
      const statusLabels = {
        [RECONCILIATION_STATUS.PENDING]: "Pendente",
        [RECONCILIATION_STATUS.APPROVED]: "Aprovado",
        [RECONCILIATION_STATUS.DIVERGENT]: "Divergente",
        [RECONCILIATION_STATUS.REJECTED]: "Rejeitado",
        [RECONCILIATION_STATUS.APPROVED_MANUAL]: "Aprovado Manualmente",
        [RECONCILIATION_STATUS.APPROVED_WITH_DISCLAIMER]:
          "Aprovado com Ressalvas",
      };
      newFilters.push({
        key: "reconciliation_status_id",
        label: "Status",
        value: statusLabels[reconciliationStatusId],
      });
    }

    if (paymentFormId !== "all") {
      const paymentFormLabels = {
        [PAYMENT_FORMS.CREDIT_CARD]: "Cartão de Crédito",
        [PAYMENT_FORMS.MONEY]: "Dinheiro",
        [PAYMENT_FORMS.PIX]: "PIX Sicredi",
        [PAYMENT_FORMS.PIX_BTG]: "PIX BTG",
        [PAYMENT_FORMS.PIX_CNPJ]: "PIX CNPJ",
        [PAYMENT_FORMS.REINVESTMENT]: "Reinvestimento",
        [PAYMENT_FORMS.VALE]: "Vale Compra",
        [PAYMENT_FORMS.PIX_MAQUINETA]: "Pix Control Pay",
        [PAYMENT_FORMS.INSTALLMENT_CARD]: "Cartão Parcelado",
        [PAYMENT_FORMS.DEPOSIT]: "Depósito",
        [PAYMENT_FORMS.ADVANCE_PAYMENT]: "Pagamento Antecipado",
      };
      newFilters.push({
        key: "payment_form_id",
        label: "Forma de Pagamento",
        value: paymentFormLabels[paymentFormId],
      });
    }

    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE &&
      expenseId &&
      expenseId !== "none"
    ) {
      newFilters.push({
        key: "expense_id",
        label: "Despesa",
        value: expenseId,
      });
    }

    if (activeDateFilter === "payment") {
      const periodLabel = isCustomPeriod
        ? `${format(paymentDateStart, "dd/MM/yyyy", {
            locale: ptBR,
          })} a ${format(paymentDateEnd, "dd/MM/yyyy", { locale: ptBR })}`
        : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";

      newFilters.push({
        key: "payment_date",
        label: "Data de Pagamento",
        value: periodLabel,
      });
    } else if (transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) {
      const periodLabel = isCustomPeriod
        ? `${format(dueDateStart, "dd/MM/yyyy", { locale: ptBR })} a ${format(
            dueDateEnd,
            "dd/MM/yyyy",
            { locale: ptBR }
          )}`
        : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";

      newFilters.push({
        key: "due_date",
        label: "Data de Vencimento",
        value: periodLabel,
      });
    }

    setActiveFilters(newFilters);
    setHasActiveFilters(newFilters.length > 0);
  };

  const handlePeriodChange = (value: string) => {
    setPeriodOption(value);

    if (value === "custom") {
      setIsCustomPeriod(true);
      return;
    }

    setIsCustomPeriod(false);

    if (value === "current-month") {
      const firstDayOfMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      if (activeDateFilter === "payment") {
        setPaymentDateStart(firstDayOfMonth);
        setPaymentDateEnd(today);
      } else {
        setDueDateStart(firstDayOfMonth);
        setDueDateEnd(today);
      }
    } else {
      const days = parseInt(value, 10);
      if (activeDateFilter === "payment") {
        setPaymentDateStart(days > 0 ? subDays(today, days) : today);
        setPaymentDateEnd(today);
      } else {
        setDueDateStart(days > 0 ? subDays(today, days) : today);
        setDueDateEnd(today);
      }
    }
  };

  const handleTransactionTypeChange = (value: string) => {
    setTransactionTypeValue(value);

    if (value === "all") {
      setTransactionType(undefined);
    } else {
      setTransactionType(parseInt(value, 10));
      if (parseInt(value, 10) === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
        setExpenseId("none");
        setActiveDateFilter("payment");
      }
    }
  };

  const applyFilters = async () => {
    setState("loading");
    updateActiveFilters();

    const filters: any = {};

    if (description) filters.description = description;
    if (amount) filters.amount = amount;
    if (transactionType) filters.transaction_type_id = transactionType;
    if (reconciliationStatusId !== "all")
      filters.reconciliation_status_id = reconciliationStatusId;
    if (paymentFormId !== "all") filters.payment_form_id = paymentFormId;

    if (transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) {
      if (expenseId && expenseId !== "none") filters.expense_id = expenseId;

      if (activeDateFilter === "due") {
        filters.date_start = format(dueDateStart, "yyyy-MM-dd");
        filters.date_end = format(dueDateEnd, "yyyy-MM-dd");
      } else {
        filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
        filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
      }
    } else if (transactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
      filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
      filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
    } else {
      if (activeDateFilter === "due") {
        filters.date_start = format(dueDateStart, "yyyy-MM-dd");
        filters.date_end = format(dueDateEnd, "yyyy-MM-dd");
      } else {
        filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
        filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
      }
    }

    try {
      const { data, ok } = await funcSearch(filters);
      if (ok) {
        updateFront(data?.response);
      }
    } catch (error) {
      console.error("Erro ao aplicar filtros:", error);
    } finally {
      setState("production");
    }
  };

  const clearFilters = async () => {
    setState("loading");
    setHasActiveFilters(false);
    setActiveFilters([]);

    if (!initialTransactionType) {
      setTransactionType(undefined);
      setTransactionTypeValue("all");
    }

    setPeriodOption("7");
    setIsCustomPeriod(false);
    setDescription("");
    setAmount("");
    setExpenseId("none");
    setActiveDateFilter("payment");
    setReconciliationStatusId("all");
    setPaymentFormId("all");

    const defaultDays = 7;
    setPaymentDateStart(subDays(today, defaultDays));
    setPaymentDateEnd(today);
    setDueDateStart(subDays(today, defaultDays));
    setDueDateEnd(today);

    try {
      const baseFilters = initialTransactionType
        ? { transaction_type_id: initialTransactionType }
        : {};
      const { data } = await funcSearch(baseFilters);
      updateFront(data?.response);
    } catch (error) {
      console.error("Erro ao limpar filtros:", error);
    } finally {
      setState("production");
    }
  };

  const removeFilter = async (key: string) => {
    setState("loading");

    switch (key) {
      case "transaction_type_id":
        if (!initialTransactionType) {
          setTransactionType(undefined);
          setTransactionTypeValue("all");
        }
        break;
      case "description":
        setDescription("");
        break;
      case "amount":
        setAmount("");
        break;
      case "expense_id":
        setExpenseId("none");
        break;
      case "reconciliation_status_id":
        setReconciliationStatusId("all");
        break;
      case "payment_form_id":
        setPaymentFormId("all");
        break;
      case "payment_date":
        setActiveDateFilter("payment");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setPaymentDateStart(subDays(today, 7));
        setPaymentDateEnd(today);
        break;
      case "due_date":
        setActiveDateFilter("due");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setDueDateStart(subDays(today, 7));
        setDueDateEnd(today);
        break;
    }

    const updatedFilters = activeFilters.filter((filter) => filter.key !== key);
    setActiveFilters(updatedFilters);
    setHasActiveFilters(updatedFilters.length > 0);

    applyFilters();
  };

  useEffect(() => {
    applyFilters();
  }, []);

  return (
    <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">Filtros</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="mb-3 flex flex-wrap gap-1">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="flex items-center gap-1 py-1"
            >
              <span className="text-xs">
                {filter.label}: {filter.value}
              </span>
              <Button
                size="sm"
                onClick={() => removeFilter(filter.key)}
                className="h-4 w-4 p-0 ml-1"
                disabled={
                  filter.key === "transaction_type_id" &&
                  initialTransactionType !== undefined
                }
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      <Separator className="mb-3" />

      <div className="flex flex-wrap gap-3 mb-4">
        {!initialTransactionType && (
          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-xs text-muted-foreground">Tipo</label>
            <Select
              value={transactionTypeValue}
              onValueChange={handleTransactionTypeChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem
                  value={TRANSACTION_TYPES.ACCOUNTS_PAYABLE.toString()}
                >
                  Contas a Pagar
                </SelectItem>
                <SelectItem
                  value={TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE.toString()}
                >
                  Contas a Receber
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Descrição</label>
          <Input
            placeholder="Filtrar por descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-9"
          />
        </div>

        <div className="flex flex-col gap-1 w-[120px]">
          <label className="text-xs text-muted-foreground">Valor</label>
          <Input
            placeholder="Valor"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9"
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select
            value={reconciliationStatusId}
            onValueChange={setReconciliationStatusId}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value={RECONCILIATION_STATUS.PENDING.toString()}>
                Pendente
              </SelectItem>
              <SelectItem value={RECONCILIATION_STATUS.APPROVED.toString()}>
                Aprovado
              </SelectItem>
              <SelectItem value={RECONCILIATION_STATUS.DIVERGENT.toString()}>
                Divergente
              </SelectItem>
              <SelectItem value={RECONCILIATION_STATUS.REJECTED.toString()}>
                Rejeitado
              </SelectItem>
              <SelectItem
                value={RECONCILIATION_STATUS.APPROVED_WITH_DISCLAIMER.toString()}
              >
                Aprovado com Ressalvas
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">
            Forma de Pagamento
          </label>
          <Select value={paymentFormId} onValueChange={setPaymentFormId}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todas as formas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as formas</SelectItem>
              <SelectItem value={PAYMENT_FORMS.CREDIT_CARD.toString()}>
                Cartão de Crédito
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.MONEY.toString()}>
                Dinheiro
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.PIX.toString()}>PIX Sicredi</SelectItem>
              <SelectItem value={PAYMENT_FORMS.PIX_BTG.toString()}>
                PIX BTG
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.PIX_CNPJ.toString()}>
                PIX CNPJ
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.REINVESTMENT.toString()}>
                Reinvestimento
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.VALE.toString()}>
                Vale Compra
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.PIX_MAQUINETA.toString()}>
                Pix Control Pay
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.INSTALLMENT_CARD.toString()}>
                Cartão Parcelado
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.DEPOSIT.toString()}>
                Depósito
              </SelectItem>
              <SelectItem value={PAYMENT_FORMS.ADVANCE_PAYMENT.toString()}>
                Pagamento Antecipado
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {(transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE ||
        initialTransactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) && (
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">
              Tipo de Despesa
            </label>
            <Select value={expenseId} onValueChange={setExpenseId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Todos os tipos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">
              Filtrar por data de
            </label>
            <Select
              value={activeDateFilter}
              onValueChange={(value: "payment" | "due") =>
                setActiveDateFilter(value)
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Tipo de data" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payment">Data de Pagamento</SelectItem>
                <SelectItem value="due">Data de Vencimento</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-xs text-muted-foreground">Período</label>
          <Select value={periodOption} onValueChange={handlePeriodChange}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecionar período" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((option) => (
                <SelectItem
                  key={option.days?.toString()}
                  value={option.days?.toString() || "custom"}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isCustomPeriod && (
          <>
            <div className="flex flex-col gap-1 min-w-[140px]">
              <span className="text-xs text-muted-foreground">
                Data Inicial
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !activeDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {activeDateFilter === "payment"
                      ? format(paymentDateStart, "dd/MM/yyyy", { locale: ptBR })
                      : format(dueDateStart, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={
                      activeDateFilter === "payment"
                        ? paymentDateStart
                        : dueDateStart
                    }
                    onSelect={(date) => {
                      if (date) {
                        if (activeDateFilter === "payment") {
                          setPaymentDateStart(date);
                        } else {
                          setDueDateStart(date);
                        }
                      }
                    }}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <span className="text-xs text-muted-foreground">Data Final</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !activeDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {activeDateFilter === "payment"
                      ? format(paymentDateEnd, "dd/MM/yyyy", { locale: ptBR })
                      : format(dueDateEnd, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={
                      activeDateFilter === "payment"
                        ? paymentDateEnd
                        : dueDateEnd
                    }
                    onSelect={(date) => {
                      if (date) {
                        if (activeDateFilter === "payment") {
                          setPaymentDateEnd(date);
                        } else {
                          setDueDateEnd(date);
                        }
                      }
                    }}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>

      <Button onClick={applyFilters} className="w-full">
        <Filter className="h-4 w-4 mr-2" />
        Aplicar Filtros
      </Button>
    </div>
  );
};
